import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import ShopRes from "types/res/shop/ShopRes";
import styled from "styled-components";
import CommonTable, { CommonTableProps } from "components/CommonTable";
import {
  addAdditionalTime,
  deleteAdditionalTime,
  fetchAdditionalTime,
  updateAdditionalTime,
} from "redux/actions/additionalTime";
import AdditionalTimeRes from "types/res/additionalTime/AdditionalTimeRes";
import { fetchShops } from "redux/actions/shop";
import { FormType } from "components/FormField";
import CreateAdditionalTimeReq from "../../types/req/additionalTime/CreateAdditionalTimeReq";
import UpdateAdditionalTimeReq from "../../types/req/additionalTime/UpdateAdditionalTimeReq";
import { fetchNotelClass } from "redux/actions/notelClass";

const AdditionalTime: React.FC = () => {
  const { t } = useTranslation("master");
  const dispatch = useDispatch();
  const companyId = useSelector((state) => state.account.staff.companyId);
  const shops = useSelector((state) => state.shop);
  const notelClasses = useSelector((state) => state.notelClass);
  const additionalTime = useSelector((state) => state.additionalTime);

  useEffect(() => {
    dispatch(fetchShops(companyId));
    dispatch(fetchNotelClass(companyId));
    dispatch(fetchAdditionalTime(companyId));
  }, [companyId, dispatch]);

  const headers: { key: keyof AdditionalTimeRes; label: string }[] = [
    { key: "time", label: t("additionalTime.headers.time") },
    { key: "name", label: t("additionalTime.headers.name") },
    { key: "totalFee", label: t("additionalTime.headers.totalFee") },
    { key: "shopFee", label: t("additionalTime.headers.shopFee") },
    { key: "castFee", label: t("additionalTime.headers.castFee") },
    { key: "isGuestShow", label: t("additionalTime.headers.isGuestShow") },
    { key: "shops", label: t("additionalTime.headers.shops") },
    { key: "notelClasses", label: t("additionalTime.headers.notelClasses") },
  ];

  const forms = [
    {
      label: t("additionalTime.forms.time"),
      key: "time",
      type: FormType.Number,
    },
    {
      label: t("additionalTime.forms.name"),
      key: "name",
      type: FormType.Text,
    },
    {
      label: t("additionalTime.forms.totalFee"),
      key: "totalFee",
      type: FormType.Number,
    },
    {
      label: t("additionalTime.forms.shopFee"),
      key: "shopFee",
      type: FormType.Number,
    },
    {
      label: t("additionalTime.forms.castFee"),
      key: "castFee",
      type: FormType.Number,
    },
    {
      label: t("additionalTime.forms.isGuestShow"),
      key: "isGuestShow",
      type: FormType.Boolean,
      options: [
        { id: "0", name: t("additionalTime.options.hidden") },
        { id: "1", name: t("additionalTime.options.shown") },
      ],
    },
    {
      label: t("additionalTime.forms.shops"),
      key: "shops",
      type: FormType.MultiOptions,
      options: shops.map((s) => ({ id: Number(s.shopId), name: s.name })),
      value: (val: any) => val.map((s: any) => s.shopId),
    },
    {
      label: t("additionalTime.forms.notelClasses"),
      key: "notelClasses",
      type: FormType.MultiOptions,
      options: notelClasses.map((s) => ({
        id: Number(s.notelClassId),
        name: s.name,
      })),
      value: (val: any) => val.map((s: any) => s.notelClassId),
    },
  ];

  return (
    <>
      <AdditionalTimeTable
        title={t("additionalTime.title")}
        formId="additionalTimeForm"
        forms={forms}
        addType={CreateAdditionalTimeReq}
        updateType={UpdateAdditionalTimeReq}
        addFunc={(formData) => addAdditionalTime(companyId, formData)}
        updateFunc={(formData) => updateAdditionalTime(companyId, formData)}
        deleteFunc={(item) =>
          deleteAdditionalTime(companyId, {
            additionalTimeId: item?.additionalTimeId,
          })
        }
        rows={headers}
        data={additionalTime}
        values={[
          (s) => s.time,
          (s) => s.name || t("additionalTime.values.unset"),
          (s) => s.totalFee,
          (s) => s.shopFee,
          (s) => s.castFee,
          (s) => (s.isGuestShow ? t("additionalTime.values.shown") : t("additionalTime.values.hidden")),
          (s) =>
            s.shops
              .map(
                (shopRes: ShopRes) =>
                  shops.find((shop) => shop.shopId === shopRes.shopId)?.name
              )
              .join(",") || t("additionalTime.values.notAssigned"),
          (s) =>
            s.notelClasses.map((notelClass) => notelClass.name).join(",") ||
            t("additionalTime.values.notAssigned"),
        ]}
      />
    </>
  );
};

const AdditionalTimeTable = styled<
  React.FC<
    CommonTableProps<
      AdditionalTimeRes,
      CreateAdditionalTimeReq,
      UpdateAdditionalTimeReq
    >
  >
>(CommonTable)`
  margin-top: 24px;
`;

export default AdditionalTime;
